import { useState, useEffect } from 'react';
import { captureException } from '@sentry/react';
import { doc, getDoc } from 'firebase/firestore';

import { ConsultType, ConsultWaitTime, ConsultWaitTimeData } from '@shared/consult/index.ts';

import { db } from '../firebase-config';
import {
  TREATMENT_TYPES_INVERTED,
  FORM_CATEGORIES_INVERTED,
  CONSULT_TRANSLATOR_INVERTED,
  ensureCorrectPluralForm,
  consultPriceTranslator
} from '../utils';

import type { GeneralPatientInfo } from '../types/generalPatientInfo.temp.types.ts';
import { waitTimeRoles, type WaitTimeRole } from '../components/admin/UpdateConsultWaitTime/helpers.ts';

type TriageConsultAcknowledgedTime =
  GeneralPatientInfo['triageConsultTimeAcknowledged'][WhiteSpaceToScreamingSnakeCase<FormCategory>];

type GetConsultTimeParams = {
  formCategory: FormCategory;
  consultIds?: string[];
  consultTimeData: ConsultWaitTimeData;
};

export type ConsultTimeDisplayText = Record<WaitTimeRole, string | undefined>;

const DEFAULT_CONSULT_TIME_DISPLAY: ConsultTimeDisplayText = {
  DOCTOR: undefined,
  NURSE: undefined,
};

/**
 * Returns a human-readable consult time string (e.g. "28 days").
 * If consult wait time data is unsatisfactory, returns undefined.
 */
const fetchConsultTimeMessage = (consultWaitTime?: ConsultWaitTime): string | undefined => {
  if (!consultWaitTime || Object.keys(consultWaitTime).length === 0) {
    return undefined;
  }

  const { duration, durationUnit } = consultWaitTime;

  if (!duration || !durationUnit) {
    return undefined;
  }

  return `${duration} ${ensureCorrectPluralForm({
    count: duration,
    word: durationUnit.toLowerCase(),
  })}`;
};

/**
 * Derives a displayable consult wait time per role (DOCTOR, NURSE)
 * based on form category and consult wait time data.
 */
const getConsultTimeDisplayText = ({
  formCategory,
  consultIds,
  consultTimeData,
}: GetConsultTimeParams): ConsultTimeDisplayText => {
  /**
   * 🧍 INITIAL CONSULT (non-checkup)
   */
  if (formCategory !== ConsultType.CHECKUP) {
    const categoryKey = TREATMENT_TYPES_INVERTED?.[formCategory];
    const consultWaitTime = consultTimeData?.[categoryKey];

    const doctorDisplayText = fetchConsultTimeMessage(consultWaitTime?.DOCTOR?.initialConsultWaitTime);
    const nurseDisplayText = fetchConsultTimeMessage(consultWaitTime?.NURSE?.initialConsultWaitTime);

    return { DOCTOR: doctorDisplayText, NURSE: nurseDisplayText };
  }

  // Early return if no consult IDs provided for checkup type
  if (!consultIds || consultIds.length === 0) {
    captureException(new Error('Missing required consultIds for checkup consult type processing'), {
      tags: { issueIn: 'useConsultTime.getConsultTime' },
      extra: { formCategory, consultIds, consultTimeData },
    });

    return DEFAULT_CONSULT_TIME_DISPLAY;
  }

  /**
   * 🩹 CHECKUP CONSULT
   */

  // Aggregate all consult times by role
  const consultData = consultIds.reduce<Record<WaitTimeRole, ConsultWaitTime[]>>(
    (acc, consultId) => {
      const healthCategory = CONSULT_TRANSLATOR_INVERTED[consultId];

      /**
       * NOTE: Skincare is deprecated and unsupported.
       * If it somehow appears, fallback to acne's consult time.
       */
      if (!healthCategory) {
        return acc;
      }

      const healthCategoryKey = FORM_CATEGORIES_INVERTED[healthCategory];

      if (!healthCategoryKey) {
        return acc;
      }

      const doctorWaitTime = consultTimeData[healthCategoryKey]?.DOCTOR?.initialConsultWaitTime;
      const nurseWaitTime = consultTimeData[healthCategoryKey]?.NURSE?.initialConsultWaitTime;

      return {
        DOCTOR: doctorWaitTime && 'timeInMins' in doctorWaitTime ? [...acc.DOCTOR, doctorWaitTime] : acc.DOCTOR,
        NURSE: nurseWaitTime && 'timeInMins' in nurseWaitTime ? [...acc.NURSE, nurseWaitTime] : acc.NURSE,
      };
    },
    { DOCTOR: [], NURSE: [] },
  );

  // Determine longest consult per role
  const longestConsultPerRole = waitTimeRoles.reduce<Record<WaitTimeRole, ConsultWaitTime | undefined>>(
    (acc, role) => {
      const longest = consultData[role].reduce<ConsultWaitTime | undefined>(
        (max, consult) => (consult.timeInMins > (max?.timeInMins ?? -Infinity) ? consult : max),
        undefined,
      );
      return { ...acc, [role]: longest };
    },
    { DOCTOR: undefined, NURSE: undefined },
  );

  // Compute display text for checkup consults
  return {
    DOCTOR: fetchConsultTimeMessage(longestConsultPerRole.DOCTOR),
    NURSE: fetchConsultTimeMessage(longestConsultPerRole.NURSE),
  };
};

export type UseConsultTimeParams<T extends boolean | undefined> = {
  consultIds?: string[];
  formCategory?: FormCategory;
  shouldGetPatientConsultTime?: T;
} & (T extends true ? { patientId: string } : {});

type UseConsultTimeReturn<T extends boolean | undefined> = {
  consultTime: ConsultTimeDisplayText;
  isLoading: boolean;
  isNicotineCheckup: boolean;
} & (T extends true
  ? {
      isTriageAcknowledged: boolean;
      patientTriageAcknowledgedData: TriageConsultAcknowledgedTime;
    }
  : {});

// Overloads for correct return type inference
export function useConsultTime(props: UseConsultTimeParams<true>): UseConsultTimeReturn<true>;
export function useConsultTime(props: UseConsultTimeParams<false | undefined>): UseConsultTimeReturn<false | undefined>;

export function useConsultTime<T extends boolean | undefined>(props: UseConsultTimeParams<T>): UseConsultTimeReturn<T> {
  const { consultIds, formCategory, shouldGetPatientConsultTime } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [consultTime, setConsultTime] = useState(DEFAULT_CONSULT_TIME_DISPLAY);
  const [patientTriageAcknowledgedData, setPatientTriageAcknowledgedData] = useState<TriageConsultAcknowledgedTime>(
    {} as TriageConsultAcknowledgedTime,
  );

  const isNicotineCheckup =
    formCategory === ConsultType.CHECKUP && consultIds?.length === 1 && consultIds[0] === consultPriceTranslator.nicotine;
  
  // Type guard to safely extract patientId for dependency array
  const patientId =
    shouldGetPatientConsultTime && 'patientId' in props ? (props as UseConsultTimeParams<true>).patientId : undefined;

  useEffect(() => {
    const getConsultTime = async () => {
      setIsLoading(true);
      try {
        if (!formCategory) {
          return;
        }

        const docSnap = await getDoc(doc(db, 'public', 'system_status_info'));

        if (!docSnap.exists()) {
          return;
        }

        const consultTimeData: ConsultWaitTimeData = docSnap.data()?.consultProcessingTime ?? {};
        setConsultTime(getConsultTimeDisplayText({ formCategory, consultIds, consultTimeData }));

        if (!shouldGetPatientConsultTime) {
          return;
        }

        if (!patientId) {
          throw new Error('Missing patientId for fetching patient consult time acknowledge data');
        }

        /**
         * getting patient triage acknowledge data if requested
         */

        const patientDocSnap = await getDoc(doc(db, 'patients', patientId));

        if (!patientDocSnap.exists()) {
          throw new Error(`Patient document does not exist for patientId: ${patientId}`);
        }

        const patientData = patientDocSnap.data() as GeneralPatientInfo;
        const formCategoryKey = FORM_CATEGORIES_INVERTED[formCategory];
        const triageConsultTimeAcknowledgement = patientData?.triageConsultTimeAcknowledged?.[formCategoryKey] ?? {};

        setPatientTriageAcknowledgedData(triageConsultTimeAcknowledgement);
      } catch (error) {
        captureException(error, {
          tags: { issueIn: 'useConsultTime.getConsultTime' },
          extra: { props },
        });
      } finally {
        setIsLoading(false);
      }
    };

    getConsultTime();
  }, [formCategory, consultIds, shouldGetPatientConsultTime, patientId]);

  if (shouldGetPatientConsultTime) {
    return {
      consultTime,
      isLoading,
      patientTriageAcknowledgedData,
      isTriageAcknowledged: Boolean(patientTriageAcknowledgedData?.acceptedAt),
      isNicotineCheckup,
    };
  }

  return {
    consultTime,
    isLoading,
    isNicotineCheckup,
  } as UseConsultTimeReturn<T>;
}
